import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Broadcast, ArrowRight, TrendUp, TrendDown, Minus,
  UsersThree, ChartLineUp, Sparkle, CurrencyDollar, CursorClick,
} from "@phosphor-icons/react";

// Marketing Intelligence Phase 3 — Social Media view.
// Reads /api/marketing/social. Only surfaces platforms with real data.

function fmtNum(n) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}
function fmtMoney(n) {
  return `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
function fmtRate(v) {
  return v === null || v === undefined ? "—" : `${v}%`;
}

const TREND_META = {
  up:   { Icon: TrendUp,   color: "text-emerald-300", label: "up" },
  down: { Icon: TrendDown, color: "text-rose-300",    label: "down" },
  flat: { Icon: Minus,     color: "text-ink-secondary", label: "flat" },
};

export default function SocialMedia() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketing/social", { params: { days: 30 } });
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, currentOrg?.org_id]);

  return (
    <div className="p-8 md:p-12 max-w-[1400px]" data-testid="social-media-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary flex items-center gap-1.5">
            <Broadcast weight="duotone" className="h-3 w-3" /> Marketing · Social
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Social media</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-2xl">
            Per-platform activity over the last 30 days, with trend vs. the prior 30 days. Only platforms
            that actually have data for this workspace are listed — no empty tiles.
          </p>
        </div>
        <Link to="/marketing" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-graphite-950 px-4 py-2 text-sm text-ink-primary hover:bg-graphite-900"
          data-testid="social-media-back">
          Marketing overview <ArrowRight weight="bold" className="h-3.5 w-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-ink-secondary" data-testid="social-media-loading">
          Loading social breakdown…
        </div>
      ) : !data ? (
        <div className="mt-10 text-sm text-ink-secondary">Social breakdown unavailable.</div>
      ) : !data.has_data ? (
        <div className="mt-10 rounded-md border border-white/10 bg-graphite-900 p-12 text-center"
          data-testid="social-media-empty">
          <Broadcast weight="duotone" className="mx-auto h-10 w-10 text-ink-secondary/60" />
          <div className="mt-4 font-display text-2xl tracking-tight">No social data yet</div>
          <p className="mt-2 text-sm text-ink-secondary max-w-md mx-auto">
            Connect the Metricool inbound API to start streaming per-platform reach, engagement and spend
            into this view.
          </p>
        </div>
      ) : (
        <>
          <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-ink-secondary" data-testid="social-window">
            Last {data.days} days · {data.platforms.length} platform{data.platforms.length === 1 ? "" : "s"} · trend vs. previous {data.days} days
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="social-cards">
            {data.platforms.map((p) => <PlatformCard key={p.platform} p={p} />)}
          </div>
        </>
      )}
    </div>
  );
}

function PlatformCard({ p }) {
  const meta = TREND_META[p.trend] || TREND_META.flat;
  const TrendIcon = meta.Icon;
  const pctText = p.engagement_pct_change === null || p.engagement_pct_change === undefined
    ? "—"
    : `${p.engagement_pct_change > 0 ? "+" : ""}${p.engagement_pct_change}%`;
  return (
    <section
      data-testid={`social-platform-${p.platform}`}
      className="rounded-md border border-white/10 bg-graphite-900 p-6 flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Platform</div>
          <h2 className="mt-0.5 font-display text-xl tracking-tight">{p.platform}</h2>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-graphite-950 px-3 py-1 text-xs ${meta.color}`}
          data-testid={`social-platform-${p.platform}-trend`}
          data-trend={p.trend}
        >
          <TrendIcon weight="fill" className="h-3.5 w-3.5" />
          <span className="tabular-nums">{pctText}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <StatTile Icon={UsersThree} label="Reach" value={fmtNum(p.reach)} testid={`social-${p.platform}-reach`} />
        <StatTile Icon={ChartLineUp} label="Impressions" value={fmtNum(p.impressions)} testid={`social-${p.platform}-impressions`} />
        <StatTile Icon={Sparkle} label="Engagement" value={fmtNum(p.engagement)} testid={`social-${p.platform}-engagement`} tone="text-fuchsia-300" />
        <StatTile
          Icon={ChartLineUp}
          label="Engagement rate"
          value={fmtRate(p.engagement_rate)}
          sub={p.engagement_rate === null ? "no impressions" : "engagement ÷ impressions"}
          testid={`social-${p.platform}-engrate`}
          tone="text-emerald-200"
        />
        <StatTile Icon={CursorClick} label="Clicks" value={fmtNum(p.clicks)} testid={`social-${p.platform}-clicks`} />
        <StatTile Icon={UsersThree} label="Leads" value={fmtNum(p.leads)} testid={`social-${p.platform}-leads`} />
        <StatTile Icon={CurrencyDollar} label="Spend" value={fmtMoney(p.spend)} testid={`social-${p.platform}-spend`} tone="text-emerald-200" />
        <StatTile Icon={ChartLineUp} label="Conversions" value={fmtNum(p.conversions)} testid={`social-${p.platform}-conversions`} />
      </div>
    </section>
  );
}

function StatTile({ Icon, label, value, sub, tone, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/10 bg-graphite-950/40 p-3">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-ink-secondary">{label}</div>
        <Icon weight="duotone" className={`h-3.5 w-3.5 ${tone || "text-ink-primary/80"}`} />
      </div>
      <div className={`mt-1 font-display text-lg tracking-tight tabular-nums ${tone || ""}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[10px] text-ink-secondary/70">{sub}</div>}
    </div>
  );
}
